'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_PERMISSIONS, getPermissionForRoute } from '@/config/permissions';

/**
 * usePermissions Hook
 * 
 * Provides permission checking for the current user.
 * Reads permissions from the user object in Redux store (synced from cookie/API).
 * 
 * Usage:
 *   const { hasPermission, permissions, isAdmin } = usePermissions();
 *   if (hasPermission('posts')) { ... }
 */
export function usePermissions() {
  const currentUser = useSelector((state) => state.user.user);

  const role = currentUser?.role || null;
  const isAdmin = role === 'admin';
  const isMember = role === 'member';

  // Get permissions from user object (set by /me endpoint) or use defaults
  const permissions = useMemo(() => {
    if (!currentUser) return [];

    // If permissions array exists on the user object, use it
    if (Array.isArray(currentUser.permissions) && currentUser.permissions.length > 0) {
      return currentUser.permissions;
    }

    // Fallback to defaults based on role
    return DEFAULT_PERMISSIONS[role] || [];
  }, [currentUser, role]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permissionKey) => {
      if (!currentUser) return false;
      // Admin always has all permissions
      if (isAdmin) return true;
      return permissions.includes(permissionKey);
    },
    [currentUser, isAdmin, permissions]
  );

  /**
   * Check if user has permission for a specific route
   */
  const hasRoutePermission = useCallback(
    (pathname) => {
      if (!currentUser) return false;
      if (isAdmin) return true;
      
      const requiredPermission = getPermissionForRoute(pathname);
      return permissions.includes(requiredPermission);
    },
    [currentUser, isAdmin, permissions]
  );

  /**
   * Check if user has any of the given permissions
   */
  const hasAnyPermission = useCallback(
    (permissionKeys) => {
      if (!currentUser) return false;
      if (isAdmin) return true;
      return permissionKeys.some((key) => permissions.includes(key));
    },
    [currentUser, isAdmin, permissions]
  );

  /**
   * Check if user has all of the given permissions
   */
  const hasAllPermissions = useCallback(
    (permissionKeys) => {
      if (!currentUser) return false;
      if (isAdmin) return true;
      return permissionKeys.every((key) => permissions.includes(key));
    },
    [currentUser, isAdmin, permissions]
  );

  return {
    permissions,
    role,
    isAdmin,
    isMember,
    hasPermission,
    hasRoutePermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoaded: !!currentUser,
  };
}

export default usePermissions;
